import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@artosera/ui';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.js';
import { cleMetier, dansLaPortee } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { useMutation } from '../../api/mutation.js';
import { useReservations } from '../../api/reservations.js';

/** La levée ferme la réservation et libère son statut dans la même transaction. */
export function LeverReservationOeuvre({
  galerieSlug,
  oeuvreId,
}: {
  galerieSlug: string;
  oeuvreId: string;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const queryClient = useQueryClient();
  const reservations = useReservations(galerieSlug, { oeuvreId, etat: ['active'] });
  // Les réservations d’exemplaires se lèvent sur leur cible, jamais en choisissant
  // arbitrairement l’un des exemplaires depuis la fiche de l’œuvre mère.
  const active = reservations.data?.reservations.find(
    (reservation) =>
      reservation.oeuvreId === oeuvreId &&
      reservation.etat === 'active' &&
      reservation.exemplaireId === null,
  );
  const lever = useMutation(
    dansLaPortee({
      mutationFn: (id: string, appel) =>
        api.post(
          `/g/${encodeURIComponent(galerieSlug)}/reservations/${id}/lever`,
          {},
          { galerie: galerieSlug, ...appel },
        ),
      onSuccess: () => {
        for (const domaine of ['reservations', 'oeuvres']) {
          void queryClient.invalidateQueries({ queryKey: cleMetier(galerieSlug, domaine) });
        }
      },
    }),
  );
  const erreur = lever.error ?? reservations.error;
  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        disabled={!active || reservations.isFetching}
        loading={lever.isPending}
        onClick={() => {
          if (active) lever.mutate(active.id);
        }}
      >
        {t('regie:oeuvres.statutMenu.evenements.reservation_expiree')}
      </Button>
      {erreur ? (
        <p role="alert">{estApiError(erreur) ? erreur.message : t('commun:erreurReseau')}</p>
      ) : null}
    </>
  );
}
